import React from "react";
import {
  Card,
  Container,
  Title,
  Stack,
  Text,
  Group,
  Badge,
  Center,
  Loader,
  Divider,
  ScrollArea,
} from "@mantine/core";
import { useApiUrl, useCustom } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { ZendeskAuthor, ZendeskComment } from "entities/zendesk";

export const ZendeskShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const apiUrl = useApiUrl();

  const { data: commentsResponse, isLoading } = useCustom<ZendeskComment[]>({
    url: `${apiUrl}/zendesk/tickets/${id}/comments`,
    method: "get",
  });

  const comments = commentsResponse?.data ?? [];

  const authorIds = Array.from(new Set(comments.map((c) => c.author_id)));

  const { data: authors } = useCustom<ZendeskAuthor[]>({
    url: `${apiUrl}/zendesk/authors`,
    method: "get",
    config: {
      query: {
        author_ids: authorIds.join(","),
      },
    },
    queryOptions: {
      enabled: authorIds.length > 0,
    },
  });

  return (
    <Container size="md" py="xl">
      <Title order={2} mb="lg">
        Zendesk Ticket Comments
      </Title>

      <Card withBorder shadow="sm" p="lg" radius="md">
        <Stack spacing="md">
          <Group position="apart">
            <div>
              <Text fw={500} size="lg">
                Comments for ticket #{id}
              </Text>
              <Text c="dimmed" size="sm">
                Loaded from Zendesk comments API
              </Text>
            </div>
          </Group>

          {isLoading && (
            <Center py="xl">
              <Loader />
            </Center>
          )}

          {!isLoading && comments.length === 0 && (
            <Center py="xl">
              <Text c="dimmed">No comments found for this ticket.</Text>
            </Center>
          )}

          {!isLoading && comments.length > 0 && (
            <ScrollArea style={{ height: 520 }}>
              <Stack spacing="md">
                {comments.map((c) => (
                  <Card
                    key={c.id}
                    shadow="xs"
                    radius="sm"
                    withBorder
                    bg={c.public ? "gray.0" : "yellow.0"}
                  >
                    <Group position="apart" align="flex-start">
                      <div>
                        <Text fw={600}>
                          Author Name:{" "}
                          {authors?.data?.find(
                            (author) => author.id === c.author_id
                          )?.name ?? c.author_id}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {new Date(c.created_at).toLocaleString()}
                        </Text>
                      </div>
                      <Badge
                        color={c.public ? "green" : "gray"}
                        variant="filled"
                      >
                        {c.public ? "Public" : "internal"}
                      </Badge>
                    </Group>

                    <Divider my="sm" />

                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          c.html_body ??
                          escapeHtml(c.body ?? c.plain_body ?? ""),
                      }}
                    />
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
          )}
        </Stack>
      </Card>
    </Container>
  );
};

function escapeHtml(text: string) {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export default ZendeskShow;
